import React, { useState, useEffect } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from '../../Providers/FloatMessage';
import {
    GetAdminConfigBL,
    UpdateAdminConfigBL,
    compressImageFile,
    getDefaultPrizes,
    generateGuid,
    DEFAULT_TERMS_TEXT_ES,
    DEFAULT_TERMS_TEXT_EN,
    DEFAULT_PRIVACY_TEXT_ES,
    DEFAULT_PRIVACY_TEXT_EN,
    DEFAULT_TERMS_TEXT_MX_ES,
    DEFAULT_TERMS_TEXT_MX_EN,
    DEFAULT_PRIVACY_TEXT_MX_ES,
    DEFAULT_PRIVACY_TEXT_MX_EN
} from '../../../utilities/BL/PersonalRoulette';

/**
 * Modal de Configuración Administrativa de Ruleta Baja Cup (PersonalRoulette)
 * Incluye 5 pestañas: Branding/Región, Mecánica, Premios (2 a 12 sectores), Legal y Tarjeta de Ganador.
 */
export const PersonalRouletteConfigModal = ({
    isOpen,
    onClose,
    roomId = null,
    onConfigUpdated = null
}) => {
    const { showMessage } = useFloatMessage();

    const [activeTab, setActiveTab] = useState('branding'); // 'branding', 'mechanics', 'prizes', 'legal', 'winnerCard'
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isCompressing, setIsCompressing] = useState(false);

    // ==========================================
    // TAB 1: BRANDING, REGIÓN Y TEXTOS GENERALES
    // ==========================================
    const [region, setRegion] = useState('MX'); // 'MX' | 'USA'
    const [titleEs, setTitleEs] = useState('Ruleta Baja Cup 2026');
    const [titleEn, setTitleEn] = useState('Baja Cup 2026 Lucky Roulette');
    const [subtitleEs, setSubtitleEs] = useState('¡Registra tus datos y gira para ganar premios exclusivos!');
    const [subtitleEn, setSubtitleEn] = useState('Register your info and spin to win exclusive prizes!');
    const [defaultLanguage, setDefaultLanguage] = useState('es');
    const [primaryColor, setPrimaryColor] = useState('#dc2626');
    const [backgroundColor, setBackgroundColor] = useState('#0f172a');
    const [backgroundImageUrl, setBackgroundImageUrl] = useState('');
    const [logoUrl, setLogoUrl] = useState('');
    const [sponsorLogoUrl, setSponsorLogoUrl] = useState('');

    // ==========================================
    // TAB 2: MECÁNICA DE JUEGO
    // ==========================================
    const [awardingMode, setAwardingMode] = useState('RANDOM'); // 'RANDOM' | 'PRIZE_QUEUE'
    const [prizeQueue, setPrizeQueue] = useState([]);
    const [currentQueueIndex, setCurrentQueueIndex] = useState(0);
    const [selectedQueuePrizeToAdd, setSelectedQueuePrizeToAdd] = useState('');
    const [winProbability, setWinProbability] = useState(35);
    const [folioPrefix, setFolioPrefix] = useState('TJ26');
    const [spinDurationMs, setSpinDurationMs] = useState(5000);
    const [allowMultipleParticipantsOnSameDevice, setAllowMultipleParticipantsOnSameDevice] = useState(true);
    const [stockDepletionStrategy, setStockDepletionStrategy] = useState('REDISTRIBUTE_WEIGHTS');

    // ==========================================
    // TAB 3: PREMIOS Y SECTORES (2 a 12)
    // ==========================================
    const [prizes, setPrizes] = useState(() => getDefaultPrizes());

    // ==========================================
    // TAB 4: CORTESÍA Y LEGAL
    // ==========================================
    const [courtesyMessageEs, setCourtesyMessageEs] = useState('La dinámica de la ruleta se encuentra en pausa o ha finalizado por hoy. ¡Gracias por tu interés!');
    const [courtesyMessageEn, setCourtesyMessageEn] = useState('The roulette dynamic is currently paused or has ended for today. Thank you for your interest!');
    const [redirectUrl, setRedirectUrl] = useState('https://www.bajacup.com');
    const [redirectButtonTextEs, setRedirectButtonTextEs] = useState('Visitar Sitio Oficial');
    const [redirectButtonTextEn, setRedirectButtonTextEn] = useState('Visit Official Website');
    const [termsAndConditionsTextEs, setTermsAndConditionsTextEs] = useState(DEFAULT_TERMS_TEXT_MX_ES);
    const [termsAndConditionsTextEn, setTermsAndConditionsTextEn] = useState(DEFAULT_TERMS_TEXT_MX_EN);
    const [privacyPolicyTextEs, setPrivacyPolicyTextEs] = useState(DEFAULT_PRIVACY_TEXT_MX_ES);
    const [privacyPolicyTextEn, setPrivacyPolicyTextEn] = useState(DEFAULT_PRIVACY_TEXT_MX_EN);

    // ==========================================
    // TAB 5: PERSONALIZACIÓN DE TARJETA DE GANADOR
    // ==========================================
    const [showVoucherQr, setShowVoucherQr] = useState(true);
    const [showParticipantInfo, setShowParticipantInfo] = useState(true);
    const [showRedemptionInstructions, setShowRedemptionInstructions] = useState(true);
    const [showDigitalFolio, setShowDigitalFolio] = useState(true);
    const [showSaveToGalleryButton, setShowSaveToGalleryButton] = useState(true);

    const handleRegionChange = (newRegion) => {
        setRegion(newRegion);
        if (newRegion === 'USA') {
            if (folioPrefix === 'TJ26' || !folioPrefix) setFolioPrefix('SD26');
            setTermsAndConditionsTextEs(DEFAULT_TERMS_TEXT_ES);
            setTermsAndConditionsTextEn(DEFAULT_TERMS_TEXT_EN);
            setPrivacyPolicyTextEs(DEFAULT_PRIVACY_TEXT_ES);
            setPrivacyPolicyTextEn(DEFAULT_PRIVACY_TEXT_EN);
        } else {
            if (folioPrefix === 'SD26' || !folioPrefix) setFolioPrefix('TJ26');
            setTermsAndConditionsTextEs(DEFAULT_TERMS_TEXT_MX_ES);
            setTermsAndConditionsTextEn(DEFAULT_TERMS_TEXT_MX_EN);
            setPrivacyPolicyTextEs(DEFAULT_PRIVACY_TEXT_MX_ES);
            setPrivacyPolicyTextEn(DEFAULT_PRIVACY_TEXT_MX_EN);
        }
    };

    // Cargar configuración existente al abrir modal
    useEffect(() => {
        if (!isOpen) return;

        const loadConfig = async () => {
            setIsLoading(true);
            try {
                const res = await GetAdminConfigBL(roomId);
                if (res.success && res.data) {
                    const cfg = res.data;
                    const loadedRegion = (cfg.region || 'MX').toUpperCase() === 'USA' ? 'USA' : 'MX';
                    setRegion(loadedRegion);

                    setTitleEs(cfg.titleEs || 'Ruleta Baja Cup 2026');
                    setTitleEn(cfg.titleEn || 'Baja Cup 2026 Lucky Roulette');
                    setSubtitleEs(cfg.subtitleEs || '¡Registra tus datos y gira para ganar premios exclusivos!');
                    setSubtitleEn(cfg.subtitleEn || 'Register your info and spin to win exclusive prizes!');
                    setDefaultLanguage(cfg.defaultLanguage || 'es');
                    setPrimaryColor(cfg.primaryColor || '#dc2626');
                    setBackgroundColor(cfg.backgroundColor || '#0f172a');
                    setBackgroundImageUrl(cfg.backgroundImageUrl || '');
                    setLogoUrl(cfg.logoUrl || '');
                    setSponsorLogoUrl(cfg.sponsorLogoUrl || '');

                    setAwardingMode(cfg.awardingMode || 'RANDOM');
                    setWinProbability(typeof cfg.winProbability === 'number' ? cfg.winProbability : 35);
                    setFolioPrefix(cfg.folioPrefix || (loadedRegion === 'USA' ? 'SD26' : 'TJ26'));
                    setSpinDurationMs(cfg.spinDurationMs || 5000);
                    setAllowMultipleParticipantsOnSameDevice(
                        cfg.allowMultipleParticipantsOnSameDevice !== undefined ? cfg.allowMultipleParticipantsOnSameDevice : true
                    );
                    setStockDepletionStrategy(cfg.stockDepletionStrategy || 'REDISTRIBUTE_WEIGHTS');
                    setCurrentQueueIndex(Number(cfg.currentQueueIndex) || 0);

                    if (cfg.prizeQueue && Array.isArray(cfg.prizeQueue)) {
                        setPrizeQueue(cfg.prizeQueue.map(item => {
                            if (typeof item === 'string') return item;
                            return item?.prizeId || item?.id || '';
                        }).filter(Boolean));
                    } else {
                        setPrizeQueue([]);
                    }

                    if (cfg.prizes && Array.isArray(cfg.prizes) && cfg.prizes.length >= 2) {
                        setPrizes(cfg.prizes);
                    } else {
                        setPrizes(getDefaultPrizes());
                    }

                    setCourtesyMessageEs(cfg.courtesyMessageEs || 'La dinámica de la ruleta se encuentra en pausa o ha finalizado por hoy. ¡Gracias por tu interés!');
                    setCourtesyMessageEn(cfg.courtesyMessageEn || 'The roulette dynamic is currently paused or has ended for today. Thank you for your interest!');
                    setRedirectUrl(cfg.redirectUrl || 'https://www.bajacup.com');
                    setRedirectButtonTextEs(cfg.redirectButtonTextEs || 'Visitar Sitio Oficial');
                    setRedirectButtonTextEn(cfg.redirectButtonTextEn || 'Visit Official Website');
                    setTermsAndConditionsTextEs(cfg.termsAndConditionsTextEs || (loadedRegion === 'USA' ? DEFAULT_TERMS_TEXT_ES : DEFAULT_TERMS_TEXT_MX_ES));
                    setTermsAndConditionsTextEn(cfg.termsAndConditionsTextEn || (loadedRegion === 'USA' ? DEFAULT_TERMS_TEXT_EN : DEFAULT_TERMS_TEXT_MX_EN));
                    setPrivacyPolicyTextEs(cfg.privacyPolicyTextEs || (loadedRegion === 'USA' ? DEFAULT_PRIVACY_TEXT_ES : DEFAULT_PRIVACY_TEXT_MX_ES));
                    setPrivacyPolicyTextEn(cfg.privacyPolicyTextEn || (loadedRegion === 'USA' ? DEFAULT_PRIVACY_TEXT_EN : DEFAULT_PRIVACY_TEXT_MX_EN));

                    setShowVoucherQr(cfg.showVoucherQr !== false && cfg.ShowVoucherQr !== false);
                    setShowParticipantInfo(cfg.showParticipantInfo !== false && cfg.ShowParticipantInfo !== false);
                    setShowRedemptionInstructions(cfg.showRedemptionInstructions !== false && cfg.ShowRedemptionInstructions !== false);
                    setShowDigitalFolio(cfg.showDigitalFolio !== false && cfg.ShowDigitalFolio !== false);
                    setShowSaveToGalleryButton(cfg.showSaveToGalleryButton !== false && cfg.ShowSaveToGalleryButton !== false);
                }
            } catch (err) {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: 'Error al consultar la configuración de la ruleta.'
                });
            } finally {
                setIsLoading(false);
            }
        };

        loadConfig();
    }, [isOpen, roomId, showMessage]);

    // Manejador de subida con compresión de imagen
    const handleFileUpload = async (e, setter) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsCompressing(true);
        try {
            const compressedBase64 = await compressImageFile(file, 1200, 1200, 0.85);
            setter(compressedBase64);
            showMessage({
                type: MESSAGE_TYPE.SUCCESS,
                text: 'Imagen optimizada y cargada con éxito.'
            });
        } catch (err) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error al procesar y comprimir la imagen.'
            });
        } finally {
            setIsCompressing(false);
        }
    };

    // Manejo de Sectores (Premios)
    const handleAddPrize = () => {
        if (prizes.length >= 12) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'La ruleta soporta un máximo de 12 sectores.' });
            return;
        }

        const newId = generateGuid();
        const palette = ['#dc2626', '#1e293b', '#f59e0b', '#2563eb', '#059669', '#7c3aed', '#db2777', '#0891b2', '#4f46e5', '#ca8a04', '#16a34a', '#475569'];
        const chosenColor = palette[prizes.length % palette.length];

        const newPrize = {
            id: newId,
            textEs: `PREMIO #${prizes.length + 1}`,
            textEn: `PRIZE #${prizes.length + 1}`,
            descriptionEs: `Descripción premio #${prizes.length + 1}`,
            descriptionEn: `Prize #${prizes.length + 1} description`,
            backgroundColor: chosenColor,
            textColor: '#ffffff',
            iconUrl: '',
            probabilityWeight: 10,
            totalStock: 50,
            awardedCount: 0,
            isWinner: true,
            sortOrder: prizes.length + 1
        };

        setPrizes([...prizes, newPrize]);
    };

    const handleRemovePrize = (index) => {
        if (prizes.length <= 2) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'La ruleta requiere al menos 2 sectores para operar.' });
            return;
        }
        const updated = prizes.filter((_, idx) => idx !== index);
        setPrizes(updated);
    };

    const handlePrizeChange = (index, field, value) => {
        const updated = [...prizes];
        updated[index] = {
            ...updated[index],
            [field]: value
        };
        setPrizes(updated);
    };

    const handlePrizeIconUpload = async (e, index) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsCompressing(true);
        try {
            const compressed = await compressImageFile(file, 400, 400, 0.90);
            handlePrizeChange(index, 'iconUrl', compressed);
            showMessage({
                type: MESSAGE_TYPE.SUCCESS,
                text: `Icono del sector #${index + 1} actualizado.`
            });
        } catch {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error al optimizar el icono del premio.'
            });
        } finally {
            setIsCompressing(false);
        }
    };

    // Guardado de Formulario
    const handleSubmit = async (e) => {
        if (e) e.preventDefault();

        if (!titleEs.trim() || !titleEn.trim()) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Los títulos en español e inglés son obligatorios.' });
            return;
        }

        if (prizes.length < 2 || prizes.length > 12) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'La ruleta debe tener entre 2 y 12 sectores configurados.' });
            return;
        }

        for (let i = 0; i < prizes.length; i++) {
            if (!prizes[i].textEs?.trim() || !prizes[i].textEn?.trim()) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: `El sector #${i + 1} debe tener texto en español e inglés.` });
                return;
            }
        }

        setIsSaving(true);
        try {
            const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
            const idMap = new Map();

            const sanitizedPrizes = prizes.map((p, idx) => {
                const hasValidGuid = p.id && guidRegex.test(p.id);
                const validId = hasValidGuid ? p.id : generateGuid();
                if (p.id) {
                    idMap.set(p.id, validId);
                }
                return {
                    ...p,
                    id: validId,
                    sortOrder: idx + 1,
                    totalStock: Number(p.totalStock) || 0,
                    probabilityWeight: Number(p.probabilityWeight) || 1
                };
            });

            const sanitizedQueue = (prizeQueue && prizeQueue.length > 0)
                ? prizeQueue.map(item => {
                    const rawId = typeof item === 'string' ? item : (item?.prizeId || item?.id || '');
                    if (!rawId) return '';
                    return idMap.get(rawId) || (guidRegex.test(rawId) ? rawId : '');
                }).filter(Boolean)
                : sanitizedPrizes.filter(p => p.isWinner).map(p => p.id).filter(Boolean);

            const payload = {
                roomId: roomId || undefined,
                region: region || 'MX',
                titleEs: titleEs.trim(),
                titleEn: titleEn.trim(),
                subtitleEs: subtitleEs.trim(),
                subtitleEn: subtitleEn.trim(),
                defaultLanguage,
                primaryColor,
                backgroundColor,
                backgroundImageUrl,
                logoUrl,
                sponsorLogoUrl,
                folioPrefix: folioPrefix.trim().toUpperCase(),
                winProbability: Number(winProbability) || 0,
                spinDurationMs: Number(spinDurationMs) || 5000,
                allowMultipleParticipantsOnSameDevice: Boolean(allowMultipleParticipantsOnSameDevice),
                stockDepletionStrategy,
                awardingMode: awardingMode || 'RANDOM',
                prizeQueue: sanitizedQueue,
                currentQueueIndex: Number(currentQueueIndex) || 0,
                prizes: sanitizedPrizes,
                courtesyMessageEs: courtesyMessageEs.trim(),
                courtesyMessageEn: courtesyMessageEn.trim(),
                redirectUrl: redirectUrl.trim(),
                redirectButtonTextEs: redirectButtonTextEs.trim(),
                redirectButtonTextEn: redirectButtonTextEn.trim(),
                termsAndConditionsTextEs: termsAndConditionsTextEs || (region === 'USA' ? DEFAULT_TERMS_TEXT_ES : DEFAULT_TERMS_TEXT_MX_ES),
                termsAndConditionsTextEn: termsAndConditionsTextEn || (region === 'USA' ? DEFAULT_TERMS_TEXT_EN : DEFAULT_TERMS_TEXT_MX_EN),
                privacyPolicyTextEs: privacyPolicyTextEs || (region === 'USA' ? DEFAULT_PRIVACY_TEXT_ES : DEFAULT_PRIVACY_TEXT_MX_ES),
                privacyPolicyTextEn: privacyPolicyTextEn || (region === 'USA' ? DEFAULT_PRIVACY_TEXT_EN : DEFAULT_PRIVACY_TEXT_MX_EN),
                showVoucherQr: Boolean(showVoucherQr),
                showParticipantInfo: Boolean(showParticipantInfo),
                showRedemptionInstructions: Boolean(showRedemptionInstructions),
                showDigitalFolio: Boolean(showDigitalFolio),
                showSaveToGalleryButton: Boolean(showSaveToGalleryButton)
            };

            const res = await UpdateAdminConfigBL(payload, roomId);
            if (res.success) {
                setPrizes(sanitizedPrizes);
                setPrizeQueue(sanitizedQueue);
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: '¡Configuración de la Ruleta guardada con éxito!'
                });
                if (onConfigUpdated) onConfigUpdated(res.data || payload);
                if (onClose) onClose();
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: res.message || 'Error al guardar la configuración de la ruleta.'
                });
            }
        } catch (err) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de red al actualizar la configuración.'
            });
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    const WIN_PRESETS = [
        { label: 'Nunca 0%', val: 0 },
        { label: 'Difícil 30%', val: 30 },
        { label: '50/50 50%', val: 50 },
        { label: 'Fácil 70%', val: 70 },
        { label: 'Siempre 100%', val: 100 }
    ];

    const DURATION_PRESETS = [3000, 5000, 7000, 10000];

    return (
        <>
            {/* Backdrop */}
            <div
                onClick={onClose}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    backgroundColor: 'rgba(0, 0, 0, 0.75)',
                    zIndex: 110000,
                    backdropFilter: 'blur(4px)'
                }}
            />

            {/* Modal Box */}
            <div
                style={{
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '96%',
                    maxWidth: '860px',
                    maxHeight: '94vh',
                    zIndex: 110001,
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                <div className="card shadow-lg border-0 rounded-4 overflow-hidden text-dark bg-white d-flex flex-column h-100">
                    {/* Header */}
                    <div
                        className="p-3 px-4 d-flex justify-content-between align-items-center text-white flex-shrink-0"
                        style={{ backgroundColor: primaryColor || '#dc2626' }}
                    >
                        <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-gear-fill fs-4 text-warning"></i>
                            <div>
                                <h5 className="mb-0 fw-bold">Configuración de Ruleta Baja Cup</h5>
                                <small className="opacity-75">Ajustes generales, probabilidades, sectores y marco legal</small>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            onClick={onClose}
                            disabled={isSaving}
                        ></button>
                    </div>

                    {/* Navigation Tabs */}
                    <div className="bg-light border-bottom px-3 pt-2 flex-shrink-0">
                        <ul className="nav nav-tabs border-0 gap-1">
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link fw-semibold px-3 py-2 border-0 ${activeTab === 'branding' ? 'active bg-white text-danger border-bottom border-danger border-3' : 'text-secondary'}`}
                                    onClick={() => setActiveTab('branding')}
                                >
                                    <i className="bi bi-palette me-1.5"></i> 1. Branding y Textos
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link fw-semibold px-3 py-2 border-0 ${activeTab === 'mechanics' ? 'active bg-white text-danger border-bottom border-danger border-3' : 'text-secondary'}`}
                                    onClick={() => setActiveTab('mechanics')}
                                >
                                    <i className="bi bi-controller me-1.5"></i> 2. Mecánica de Juego
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link fw-semibold px-3 py-2 border-0 ${activeTab === 'prizes' ? 'active bg-white text-danger border-bottom border-danger border-3' : 'text-secondary'}`}
                                    onClick={() => setActiveTab('prizes')}
                                >
                                    <i className="bi bi-gift me-1.5"></i> 3. Premios ({prizes.length} Sectores)
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link fw-semibold px-3 py-2 border-0 ${activeTab === 'legal' ? 'active bg-white text-danger border-bottom border-danger border-3' : 'text-secondary'}`}
                                    onClick={() => setActiveTab('legal')}
                                >
                                    <i className="bi bi-box-arrow-up-right me-1.5"></i> 4. Cortesía y Redirección
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link fw-semibold px-3 py-2 border-0 ${activeTab === 'winnerCard' ? 'active bg-white text-danger border-bottom border-danger border-3' : 'text-secondary'}`}
                                    onClick={() => setActiveTab('winnerCard')}
                                >
                                    <i className="bi bi-card-checklist me-1.5"></i> 5. Tarjeta de Ganador
                                </button>
                            </li>
                        </ul>
                    </div>

                    {/* Body con Scroll */}
                    <div className="card-body p-4 overflow-auto flex-grow-1" style={{ maxHeight: 'calc(88vh - 160px)' }}>
                        {isLoading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-danger" role="status">
                                    <span className="visually-hidden">Cargando...</span>
                                </div>
                                <p className="text-muted small mt-2">Cargando configuración de la ruleta...</p>
                            </div>
                        ) : (
                            <form id="rouletteConfigForm" onSubmit={handleSubmit}>
                                {/* TAB 1: BRANDING, REGIÓN Y TEXTOS */}
                                {activeTab === 'branding' && (
                                    <div className="d-flex flex-column gap-3">
                                        {/* Selector de Región (México vs USA) */}
                                        <div className="p-3 bg-light rounded-3 border">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <label className="form-label fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                                                    <i className="bi bi-geo-alt-fill text-danger"></i> Región y Marco Operativo
                                                </label>
                                                <span className="badge bg-danger">
                                                    {region === 'USA' ? '🇺🇸 Estados Unidos (California)' : '🇲🇽 México (Nacional / Liga MX)'}
                                                </span>
                                            </div>
                                            <p className="text-secondary small mb-2">
                                                Selecciona el país de operación para ajustar automáticamente el marco legal aplicable, los campos obligatorios del formulario y el formato de folios.
                                            </p>
                                            <div className="row g-2">
                                                <div className="col-12 col-md-6">
                                                    <div
                                                        className={`p-2.5 px-3 rounded-3 border cursor-pointer transition-all ${
                                                            region === 'MX' ? 'bg-white border-danger shadow-sm' : 'bg-white bg-opacity-50 text-muted'
                                                        }`}
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleRegionChange('MX')}
                                                    >
                                                        <div className="form-check mb-0">
                                                            <input
                                                                className="form-check-input"
                                                                type="radio"
                                                                name="regionSelectRadio"
                                                                id="regionRadioMx"
                                                                checked={region === 'MX'}
                                                                onChange={() => handleRegionChange('MX')}
                                                            />
                                                            <label className="form-check-label fw-bold text-dark cursor-pointer" htmlFor="regionRadioMx">
                                                                🇲🇽 México (Liga MX / Baja California)
                                                            </label>
                                                        </div>
                                                        <small className="d-block text-secondary mt-1" style={{ fontSize: '0.78rem' }}>
                                                            • LFPDPPP y Derechos ARCO<br />
                                                            • Sin código postal obligatorio<br />
                                                            • Prefijo sugerido: <strong>TJ26</strong>
                                                        </small>
                                                    </div>
                                                </div>
                                                <div className="col-12 col-md-6">
                                                    <div
                                                        className={`p-2.5 px-3 rounded-3 border cursor-pointer transition-all ${
                                                            region === 'USA' ? 'bg-white border-danger shadow-sm' : 'bg-white bg-opacity-50 text-muted'
                                                        }`}
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleRegionChange('USA')}
                                                    >
                                                        <div className="form-check mb-0">
                                                            <input
                                                                className="form-check-input"
                                                                type="radio"
                                                                name="regionSelectRadio"
                                                                id="regionRadioUsa"
                                                                checked={region === 'USA'}
                                                                onChange={() => handleRegionChange('USA')}
                                                            />
                                                            <label className="form-check-label fw-bold text-dark cursor-pointer" htmlFor="regionRadioUsa">
                                                                🇺🇸 USA (California / San Diego)
                                                            </label>
                                                        </div>
                                                        <small className="d-block text-secondary mt-1" style={{ fontSize: '0.78rem' }}>
                                                            • CCPA/CPRA y Do Not Sell Opt-Out<br />
                                                            • ZIP Code obligatorio de 5 dígitos<br />
                                                            • Prefijo sugerido: <strong>SD26</strong>
                                                        </small>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="row g-3">
                                            <div className="col-12 col-md-6">
                                                <label className="form-label fw-semibold small text-secondary">Título Principal (Español)</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={titleEs}
                                                    onChange={(e) => setTitleEs(e.target.value)}
                                                    placeholder="Ruleta Baja Cup 2026"
                                                    required
                                                />
                                            </div>
                                            <div className="col-12 col-md-6">
                                                <label className="form-label fw-semibold small text-secondary">Título Principal (Inglés)</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={titleEn}
                                                    onChange={(e) => setTitleEn(e.target.value)}
                                                    placeholder="Baja Cup 2026 Lucky Roulette"
                                                    required
                                                />
                                            </div>
                                        </div>

                                        <div className="row g-3">
                                            <div className="col-12 col-md-6">
                                                <label className="form-label fw-semibold small text-secondary">Subtítulo (Español)</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={subtitleEs}
                                                    onChange={(e) => setSubtitleEs(e.target.value)}
                                                    placeholder="¡Registra tus datos y gira para ganar!"
                                                />
                                            </div>
                                            <div className="col-12 col-md-6">
                                                <label className="form-label fw-semibold small text-secondary">Subtítulo (Inglés)</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={subtitleEn}
                                                    onChange={(e) => setSubtitleEn(e.target.value)}
                                                    placeholder="Register your info and spin to win!"
                                                />
                                            </div>
                                        </div>

                                        <div className="row g-3">
                                            <div className="col-12 col-md-4">
                                                <label className="form-label fw-semibold small text-secondary">Idioma por Defecto</label>
                                                <select
                                                    className="form-select"
                                                    value={defaultLanguage}
                                                    onChange={(e) => setDefaultLanguage(e.target.value)}
                                                >
                                                    <option value="es">Español (ES)</option>
                                                    <option value="en">Inglés (EN)</option>
                                                </select>
                                            </div>
                                            <div className="col-12 col-md-4">
                                                <label className="form-label fw-semibold small text-secondary">Color Primario Temático</label>
                                                <div className="input-group">
                                                    <input
                                                        type="color"
                                                        className="form-control form-control-color"
                                                        value={primaryColor}
                                                        onChange={(e) => setPrimaryColor(e.target.value)}
                                                    />
                                                    <input
                                                        type="text"
                                                        className="form-control font-monospace"
                                                        value={primaryColor}
                                                        onChange={(e) => setPrimaryColor(e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-12 col-md-4">
                                                <label className="form-label fw-semibold small text-secondary">Color de Fondo Pantalla</label>
                                                <div className="input-group">
                                                    <input
                                                        type="color"
                                                        className="form-control form-control-color"
                                                        value={backgroundColor}
                                                        onChange={(e) => setBackgroundColor(e.target.value)}
                                                    />
                                                    <input
                                                        type="text"
                                                        className="form-control font-monospace"
                                                        value={backgroundColor}
                                                        onChange={(e) => setBackgroundColor(e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="border-top pt-3 mt-2">
                                            <h6 className="fw-bold text-dark mb-3"><i className="bi bi-images me-1.5"></i> Recursos Gráficos con Compresión Automática</h6>
                                            
                                            {/* Imagen de Fondo */}
                                            <div className="mb-3">
                                                <label className="form-label fw-semibold small text-secondary">Imagen de Fondo de Pantalla (Background)</label>
                                                <div className="input-group mb-1">
                                                    <input
                                                        type="file"
                                                        className="form-control"
                                                        accept="image/*"
                                                        onChange={(e) => handleFileUpload(e, setBackgroundImageUrl)}
                                                    />
                                                    {backgroundImageUrl && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-danger"
                                                            onClick={() => setBackgroundImageUrl('')}
                                                        >
                                                            <i className="bi bi-trash"></i>
                                                        </button>
                                                    )}
                                                </div>
                                                {backgroundImageUrl && (
                                                    <small className="text-success d-block text-truncate">
                                                        <i className="bi bi-check-circle-fill me-1"></i> Imagen cargada ({backgroundImageUrl.slice(0, 40)}...)
                                                    </small>
                                                )}
                                            </div>

                                            {/* Logotipo de Sala */}
                                            <div className="mb-3">
                                                <label className="form-label fw-semibold small text-secondary">Logotipo de la Sala / Club (Logo Principal)</label>
                                                <div className="input-group mb-1">
                                                    <input
                                                        type="file"
                                                        className="form-control"
                                                        accept="image/*"
                                                        onChange={(e) => handleFileUpload(e, setLogoUrl)}
                                                    />
                                                    {logoUrl && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-danger"
                                                            onClick={() => setLogoUrl('')}
                                                        >
                                                            <i className="bi bi-trash"></i>
                                                        </button>
                                                    )}
                                                </div>
                                                {logoUrl && (
                                                    <small className="text-success d-block text-truncate">
                                                        <i className="bi bi-check-circle-fill me-1"></i> Logo cargado
                                                    </small>
                                                )}
                                            </div>

                                            {/* Logotipo Patrocinador */}
                                            <div className="mb-2">
                                                <label className="form-label fw-semibold small text-secondary">Logotipo de Patrocinador Oficial (Opcional)</label>
                                                <div className="input-group mb-1">
                                                    <input
                                                        type="file"
                                                        className="form-control"
                                                        accept="image/*"
                                                        onChange={(e) => handleFileUpload(e, setSponsorLogoUrl)}
                                                    />
                                                    {sponsorLogoUrl && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-danger"
                                                            onClick={() => setSponsorLogoUrl('')}
                                                        >
                                                            <i className="bi bi-trash"></i>
                                                        </button>
                                                    )}
                                                </div>
                                                {sponsorLogoUrl && (
                                                    <small className="text-success d-block text-truncate">
                                                        <i className="bi bi-check-circle-fill me-1"></i> Patrocinador cargado
                                                    </small>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* TAB 2: MECÁNICA DE JUEGO */}
                                {activeTab === 'mechanics' && (
                                    <div className="d-flex flex-column gap-3">
                                        {/* SELECTOR DE MODO DE ASIGNACIÓN (HU-07) */}
                                        <div className="p-3 bg-light rounded-3 border">
                                            <label className="form-label fw-bold text-dark mb-2 d-flex align-items-center gap-1.5">
                                                <i className="bi bi-diagram-3-fill text-danger"></i>
                                                <span>Modo de Asignación de Premios:</span>
                                            </label>
                                            <div className="row g-2">
                                                <div className="col-12 col-md-6">
                                                    <div
                                                        onClick={() => setAwardingMode('RANDOM')}
                                                        className={`card p-3 h-100 border cursor-pointer transition-all ${
                                                            awardingMode === 'RANDOM'
                                                                ? 'border-danger border-2 bg-danger-subtle shadow-sm'
                                                                : 'bg-white hover-lift'
                                                        }`}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <div className="form-check">
                                                            <input
                                                                className="form-check-input"
                                                                type="radio"
                                                                name="radioAwardingMode"
                                                                id="modeRandom"
                                                                checked={awardingMode === 'RANDOM'}
                                                                onChange={() => setAwardingMode('RANDOM')}
                                                            />
                                                            <label className="form-check-label fw-bold text-dark cursor-pointer" htmlFor="modeRandom">
                                                                🎲 Azar Ponderado Tradicional (RANDOM)
                                                            </label>
                                                        </div>
                                                        <small className="text-secondary mt-1 ms-4 d-block">
                                                            Sorteo probabilístico en 2 pasos según porcentaje global y peso de cada sector.
                                                        </small>
                                                    </div>
                                                </div>

                                                <div className="col-12 col-md-6">
                                                    <div
                                                        onClick={() => {
                                                            setAwardingMode('PRIZE_QUEUE');
                                                            if (prizeQueue.length === 0) {
                                                                const winnersWithStock = prizes
                                                                    .filter(p => p.isWinner && ((Number(p.totalStock) || 0) - (Number(p.awardedCount) || 0) > 0))
                                                                    .map(p => String(p.id));
                                                                if (winnersWithStock.length > 0) {
                                                                    setPrizeQueue(winnersWithStock);
                                                                }
                                                            }
                                                        }}
                                                        className={`card p-3 h-100 border cursor-pointer transition-all ${
                                                            awardingMode === 'PRIZE_QUEUE'
                                                                ? 'border-warning border-2 bg-warning-subtle shadow-sm'
                                                                : 'bg-white hover-lift'
                                                        }`}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <div className="form-check">
                                                            <input
                                                                className="form-check-input"
                                                                type="radio"
                                                                name="radioAwardingMode"
                                                                id="modePrizeQueue"
                                                                checked={awardingMode === 'PRIZE_QUEUE'}
                                                                onChange={() => {
                                                                    setAwardingMode('PRIZE_QUEUE');
                                                                    if (prizeQueue.length === 0) {
                                                                        const winnersWithStock = prizes
                                                                            .filter(p => p.isWinner && ((Number(p.totalStock) || 0) - (Number(p.awardedCount) || 0) > 0))
                                                                            .map(p => String(p.id));
                                                                        if (winnersWithStock.length > 0) {
                                                                            setPrizeQueue(winnersWithStock);
                                                                        }
                                                                    }
                                                                }}
                                                            />
                                                            <label className="form-check-label fw-bold text-dark cursor-pointer" htmlFor="modePrizeQueue">
                                                                🎯 Fila Ordenada de Premios (PRIZE_QUEUE)
                                                            </label>
                                                        </div>
                                                        <small className="text-secondary mt-1 ms-4 d-block">
                                                            Secuencia estricta paso a paso (Paso 1 ➔ Paso 2 ➔ Paso N ➔ Ciclo) controlada por el operador.
                                                        </small>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* PROBABILIDAD GLOBAL DE GANAR (APLICA TANTO EN RANDOM COMO EN PRIZE_QUEUE) */}
                                        <div className="p-3 bg-light rounded-3 border">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <label className="form-label fw-bold mb-0 text-dark">
                                                    Probabilidad Global de Ganar: <span className="text-danger fs-5">{winProbability}%</span>
                                                </label>
                                                <span className="badge bg-secondary">
                                                    {awardingMode === 'PRIZE_QUEUE' ? 'Filtro de Victoria en Fila' : 'Algoritmo en 2 Pasos'}
                                                </span>
                                            </div>
                                            <input
                                                type="range"
                                                className="form-range"
                                                min="0"
                                                max="100"
                                                step="5"
                                                value={winProbability}
                                                onChange={(e) => setWinProbability(Number(e.target.value))}
                                            />
                                            <div className="d-flex flex-wrap gap-1.5 mt-2">
                                                <span className="small text-muted me-1 align-self-center">Preajustes rápidos:</span>
                                                {WIN_PRESETS.map((p) => (
                                                    <button
                                                        key={p.val}
                                                        type="button"
                                                        className={`btn btn-sm py-0.5 px-2.5 rounded-pill fw-semibold ${winProbability === p.val ? 'btn-danger text-white' : 'btn-outline-secondary'}`}
                                                        onClick={() => setWinProbability(p.val)}
                                                    >
                                                        {p.label}
                                                    </button>
                                                ))}
                                            </div>
                                            <small className="text-muted d-block mt-2">
                                                {awardingMode === 'PRIZE_QUEUE' ? (
                                                    <span>
                                                        <strong>Modo Fila:</strong> El servidor evalúa si el participante gana con este porcentaje. Si resulta ganador, se le entrega el premio en turno de la fila secuencial. Si configuras <strong>100%</strong>, todos los tiros ganarán en el orden estricto de la fila; si pones <strong>0%</strong>, nadie ganará; si pones un porcentaje intermedio (ej. <strong>30%</strong>), solo ese porcentaje de tiros ganará y avanzará el turno de la fila.
                                                    </span>
                                                ) : (
                                                    <span>
                                                        <strong>Modo Azar:</strong> El servidor primero evalúa si el aficionado gana o no con esta probabilidad. Si resulta ganador, sortea proporcionalmente entre los sectores marcados como ganadores con stock disponible.
                                                    </span>
                                                )}
                                            </small>
                                        </div>

                                        {/* SI MODO === 'PRIZE_QUEUE': CONSTRUCTOR VISUAL DE SECUENCIA (HU-07) */}
                                        {awardingMode === 'PRIZE_QUEUE' && (
                                            <div className="p-3 bg-light rounded-3 border border-warning">
                                                <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 pb-2 border-bottom gap-2">
                                                    <div>
                                                        <h6 className="fw-bold mb-0 text-dark">
                                                            <i className="bi bi-list-ol text-warning me-1"></i> Constructor de Fila de Premios ({prizeQueue.length} Pasos)
                                                        </h6>
                                                        <small className="text-secondary">
                                                            Define el orden exacto en el que se otorgarán los premios a los participantes.
                                                        </small>
                                                    </div>

                                                    <div className="d-flex align-items-center gap-1.5">
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                                                            onClick={() => {
                                                                const winnerIds = prizes
                                                                    .filter(p => p.isWinner && ((Number(p.totalStock) || 0) - (Number(p.awardedCount) || 0) > 0))
                                                                    .map(p => String(p.id));
                                                                if (winnerIds.length > 0) {
                                                                    setPrizeQueue(winnerIds);
                                                                    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Fila auto-rellenada con premios ganadores disponibles.' });
                                                                } else {
                                                                    showMessage({ type: MESSAGE_TYPE.BAD, text: 'No hay premios ganadores con stock.' });
                                                                }
                                                            }}
                                                        >
                                                            <i className="bi bi-magic text-warning"></i>
                                                            <span>Auto-generar</span>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                                                            onClick={() => {
                                                                if (prizeQueue.length > 0) {
                                                                    setPrizeQueue(prev => [...prev, ...prev]);
                                                                    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Secuencia duplicada.' });
                                                                }
                                                            }}
                                                            disabled={prizeQueue.length === 0}
                                                        >
                                                            <i className="bi bi-copy"></i>
                                                            <span>Duplicar</span>
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Previsualizador de Ciclo */}
                                                {prizeQueue.length > 0 && (
                                                    <div className="p-2.5 bg-white rounded-3 border mb-3">
                                                        <div className="small fw-bold text-secondary mb-1.5 d-flex align-items-center gap-1">
                                                            <i className="bi bi-arrow-repeat text-success"></i> Previsualizador del Ciclo:
                                                        </div>
                                                        <div className="d-flex flex-wrap align-items-center gap-1.5 small font-monospace">
                                                            {prizeQueue.map((id, idx) => {
                                                                const pObj = prizes.find(p => String(p.id) === String(id));
                                                                const remaining = (Number(pObj?.totalStock) || 0) - (Number(pObj?.awardedCount) || 0);
                                                                const isExhausted = remaining <= 0;
                                                                return (
                                                                    <React.Fragment key={`${id}_${idx}`}>
                                                                        <span className={`badge ${isExhausted ? 'bg-danger-subtle text-danger border border-danger text-decoration-line-through' : 'bg-dark text-white'}`}>
                                                                            #{idx + 1} {pObj?.textEs || 'Premio'}
                                                                        </span>
                                                                        <i className="bi bi-arrow-right text-muted opacity-50" style={{ fontSize: '0.75rem' }}></i>
                                                                    </React.Fragment>
                                                                );
                                                            })}
                                                            <span className="badge bg-warning text-dark border border-warning fw-bold">
                                                                🔄 Repetir Ciclo
                                                            </span>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Selector para Agregar Paso */}
                                                <div className="row g-2 mb-3">
                                                    <div className="col-12 col-md-9">
                                                        <select
                                                            className="form-select form-select-sm"
                                                            value={selectedQueuePrizeToAdd}
                                                            onChange={(e) => setSelectedQueuePrizeToAdd(e.target.value)}
                                                        >
                                                            <option value="">-- Seleccionar premio ganador para agregar --</option>
                                                            {prizes.filter(p => p.isWinner).map(p => {
                                                                const remaining = (Number(p.totalStock) || 0) - (Number(p.awardedCount) || 0);
                                                                const isExhausted = remaining <= 0;
                                                                return (
                                                                    <option key={p.id} value={p.id} disabled={isExhausted}>
                                                                        {p.textEs || p.textEn} {isExhausted ? '(❌ Agotado)' : `(🟢 ${remaining} disp.)`}
                                                                    </option>
                                                                );
                                                            })}
                                                        </select>
                                                    </div>
                                                    <div className="col-12 col-md-3">
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-danger w-100 fw-bold d-flex align-items-center justify-content-center gap-1"
                                                            disabled={!selectedQueuePrizeToAdd}
                                                            onClick={() => {
                                                                if (selectedQueuePrizeToAdd) {
                                                                    setPrizeQueue(prev => [...prev, String(selectedQueuePrizeToAdd)]);
                                                                    setSelectedQueuePrizeToAdd('');
                                                                }
                                                            }}
                                                        >
                                                            <i className="bi bi-plus-lg"></i>
                                                            <span>Añadir a Fila</span>
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Lista Ordenada de Pasos */}
                                                <div className="d-flex flex-column gap-1.5" style={{ maxHeight: '220px', overflowY: 'auto' }}>
                                                    {prizeQueue.length === 0 ? (
                                                        <div className="text-center py-3 text-muted small bg-white rounded border">
                                                            No hay pasos configurados. Usa "Auto-generar" o selecciona un premio arriba.
                                                        </div>
                                                    ) : (
                                                        prizeQueue.map((prizeId, idx) => {
                                                            const pObj = prizes.find(p => String(p.id) === String(prizeId));
                                                            const remaining = (Number(pObj?.totalStock) || 0) - (Number(pObj?.awardedCount) || 0);
                                                            const isExhausted = remaining <= 0;

                                                            return (
                                                                <div
                                                                    key={`${prizeId}_${idx}`}
                                                                    className={`d-flex align-items-center justify-content-between p-2 px-3 bg-white rounded border ${
                                                                        isExhausted ? 'border-danger border-opacity-50 opacity-75' : ''
                                                                    }`}
                                                                >
                                                                    <div className="d-flex align-items-center gap-2 text-truncate">
                                                                        <span className="badge bg-dark rounded-pill font-monospace" style={{ fontSize: '0.72rem' }}>
                                                                            #{idx + 1}
                                                                        </span>
                                                                        <span
                                                                            className="rounded-circle d-inline-block flex-shrink-0"
                                                                            style={{ width: '12px', height: '12px', backgroundColor: pObj?.backgroundColor || '#dc2626' }}
                                                                        ></span>
                                                                        <span className="fw-bold small text-dark text-truncate">
                                                                            {pObj?.textEs || `Premio (${prizeId})`}
                                                                        </span>
                                                                        {isExhausted ? (
                                                                            <span className="badge bg-danger text-white" style={{ fontSize: '0.65rem' }}>
                                                                                ❌ Agotado
                                                                            </span>
                                                                        ) : (
                                                                            <span className="badge bg-success-subtle text-success border border-success" style={{ fontSize: '0.65rem' }}>
                                                                                {remaining} disp.
                                                                            </span>
                                                                        )}
                                                                    </div>

                                                                    <div className="d-flex align-items-center gap-1 flex-shrink-0">
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-outline-secondary py-0 px-1.5"
                                                                            disabled={idx === 0}
                                                                            onClick={() => {
                                                                                setPrizeQueue(prev => {
                                                                                    const n = [...prev];
                                                                                    const t = n[idx - 1];
                                                                                    n[idx - 1] = n[idx];
                                                                                    n[idx] = t;
                                                                                    return n;
                                                                                });
                                                                            }}
                                                                            title="Mover arriba"
                                                                        >
                                                                            <i className="bi bi-arrow-up"></i>
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-outline-secondary py-0 px-1.5"
                                                                            disabled={idx === prizeQueue.length - 1}
                                                                            onClick={() => {
                                                                                setPrizeQueue(prev => {
                                                                                    const n = [...prev];
                                                                                    const t = n[idx + 1];
                                                                                    n[idx + 1] = n[idx];
                                                                                    n[idx] = t;
                                                                                    return n;
                                                                                });
                                                                            }}
                                                                            title="Mover abajo"
                                                                        >
                                                                            <i className="bi bi-arrow-down"></i>
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-outline-danger py-0 px-1.5 ms-1"
                                                                            onClick={() => {
                                                                                setPrizeQueue(prev => prev.filter((_, i) => i !== idx));
                                                                            }}
                                                                            title="Eliminar de la fila"
                                                                        >
                                                                            <i className="bi bi-trash"></i>
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        <div className="row g-3">
                                            {/* Prefijo Folio */}
                                            <div className="col-12 col-md-6">
                                                <label className="form-label fw-semibold small text-secondary">Prefijo de Folio / Ticket</label>
                                                <div className="input-group">
                                                    <span className="input-group-text bg-light"><i className="bi bi-tag-fill"></i></span>
                                                    <input
                                                        type="text"
                                                        className="form-control fw-bold text-uppercase"
                                                        value={folioPrefix}
                                                        maxLength={10}
                                                        onChange={(e) => setFolioPrefix(e.target.value.toUpperCase())}
                                                        placeholder="SD26"
                                                        required
                                                    />
                                                </div>
                                                <small className="text-muted">Generará folios con formato <code>#{folioPrefix}-XXXX</code></small>
                                            </div>

                                            {/* Duración de Giro */}
                                            <div className="col-12 col-md-6">
                                                <label className="form-label fw-semibold small text-secondary">Duración de Giro (Milisegundos)</label>
                                                <div className="input-group mb-1">
                                                    <span className="input-group-text bg-light"><i className="bi bi-stopwatch-fill"></i></span>
                                                    <input
                                                        type="number"
                                                        className="form-control fw-bold"
                                                        value={spinDurationMs}
                                                        min={2000}
                                                        max={15000}
                                                        step={500}
                                                        onChange={(e) => setSpinDurationMs(Number(e.target.value))}
                                                    />
                                                    <span className="input-group-text bg-light">ms</span>
                                                </div>
                                                <div className="d-flex flex-wrap gap-1">
                                                    {DURATION_PRESETS.map((ms) => (
                                                        <button
                                                            key={ms}
                                                            type="button"
                                                            className={`btn btn-sm py-0 px-2 rounded-pill ${spinDurationMs === ms ? 'btn-danger text-white' : 'btn-outline-secondary'}`}
                                                            onClick={() => setSpinDurationMs(ms)}
                                                        >
                                                            {ms / 1000}s
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Switch Múltiples Participantes Mismo Dispositivo */}
                                        <div className="p-3 bg-light rounded-3 border">
                                            <div className="form-check form-switch mb-1">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id="switchMultipleParticipants"
                                                    checked={allowMultipleParticipantsOnSameDevice}
                                                    onChange={(e) => setAllowMultipleParticipantsOnSameDevice(e.target.checked)}
                                                />
                                                <label className="form-check-label fw-bold text-dark" htmlFor="switchMultipleParticipants">
                                                    Permitir "Participar con otra persona" en el mismo dispositivo
                                                </label>
                                            </div>
                                            <small className="text-secondary d-block">
                                                Al activarlo, se muestra un botón en la pantalla final para que acompañantes o familiares puedan registrarse en el mismo smartphone sin bloquearse mutuamente.
                                            </small>
                                        </div>

                                        {/* Estrategia de agotamiento de stock */}
                                        <div className="p-3 bg-light rounded-3 border">
                                            <label className="form-label fw-bold text-dark mb-1">Estrategia al agotarse stock físico:</label>
                                            <select
                                                className="form-select"
                                                value={stockDepletionStrategy}
                                                onChange={(e) => setStockDepletionStrategy(e.target.value)}
                                            >
                                                <option value="REDISTRIBUTE_WEIGHTS">Redistribuir probabilidades entre premios con stock restante</option>
                                                <option value="SHOW_COURTESY">Asignar automáticamente casilla de cortesía / agradecimiento</option>
                                            </select>
                                        </div>
                                    </div>
                                )}

                                {/* TAB 3: PREMIOS Y SECTORES (2 a 12) */}
                                {activeTab === 'prizes' && (
                                    <div className="d-flex flex-column gap-3">
                                        <div className="d-flex justify-content-between align-items-center bg-light p-3 rounded-3 border">
                                            <div>
                                                <h6 className="fw-bold mb-0 text-dark">Sectores de la Ruleta ({prizes.length} / 12)</h6>
                                                <small className="text-secondary">Configura de 2 a 12 rebanadas con texto bilingüe, colores, stock y banderas de ganador.</small>
                                            </div>
                                            <button
                                                type="button"
                                                className="btn btn-danger btn-sm fw-bold d-flex align-items-center gap-1 shadow-sm"
                                                onClick={handleAddPrize}
                                                disabled={prizes.length >= 12}
                                            >
                                                <i className="bi bi-plus-circle"></i> Añadir Sector
                                            </button>
                                        </div>

                                        <div className="d-flex flex-column gap-3">
                                            {prizes.map((prize, idx) => (
                                                <div key={prize.id || idx} className="card border rounded-3 shadow-sm overflow-hidden">
                                                    <div className="card-header bg-light d-flex justify-content-between align-items-center py-2 px-3">
                                                        <div className="d-flex align-items-center gap-2">
                                                            <span className="badge bg-dark rounded-pill">#{idx + 1}</span>
                                                            <span className="fw-bold text-dark">{prize.textEs || `Sector #${idx + 1}`}</span>
                                                            <span className={`badge ${prize.isWinner ? 'bg-success' : 'bg-secondary'}`}>
                                                                {prize.isWinner ? '★ Ganador' : 'Cortesía'}
                                                            </span>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-danger btn-sm py-0 px-2"
                                                            onClick={() => handleRemovePrize(idx)}
                                                            disabled={prizes.length <= 2}
                                                            title="Eliminar sector"
                                                        >
                                                            <i className="bi bi-trash"></i>
                                                        </button>
                                                    </div>

                                                    <div className="card-body p-3">
                                                        <div className="row g-2 mb-2">
                                                            <div className="col-12 col-md-6">
                                                                <label className="form-label small fw-semibold text-secondary mb-1">Texto en Sector (Español)</label>
                                                                <input
                                                                    type="text"
                                                                    className="form-control form-control-sm"
                                                                    value={prize.textEs || ''}
                                                                    onChange={(e) => handlePrizeChange(idx, 'textEs', e.target.value)}
                                                                    placeholder="Ej. GORRA OFICIAL"
                                                                    required
                                                                />
                                                            </div>
                                                            <div className="col-12 col-md-6">
                                                                <label className="form-label small fw-semibold text-secondary mb-1">Texto en Sector (Inglés)</label>
                                                                <input
                                                                    type="text"
                                                                    className="form-control form-control-sm"
                                                                    value={prize.textEn || ''}
                                                                    onChange={(e) => handlePrizeChange(idx, 'textEn', e.target.value)}
                                                                    placeholder="Ej. OFFICIAL CAP"
                                                                    required
                                                                />
                                                            </div>
                                                        </div>

                                                        <div className="row g-2 mb-2">
                                                            <div className="col-12 col-md-3">
                                                                <label className="form-label small fw-semibold text-secondary mb-1">Color Fondo</label>
                                                                <div className="input-group input-group-sm">
                                                                    <input
                                                                        type="color"
                                                                        className="form-control form-control-color"
                                                                        value={prize.backgroundColor || '#dc2626'}
                                                                        onChange={(e) => handlePrizeChange(idx, 'backgroundColor', e.target.value)}
                                                                    />
                                                                    <input
                                                                        type="text"
                                                                        className="form-control font-monospace"
                                                                        value={prize.backgroundColor || '#dc2626'}
                                                                        onChange={(e) => handlePrizeChange(idx, 'backgroundColor', e.target.value)}
                                                                    />
                                                                </div>
                                                            </div>

                                                            <div className="col-12 col-md-3">
                                                                <label className="form-label small fw-semibold text-secondary mb-1">Color Texto</label>
                                                                <div className="input-group input-group-sm">
                                                                    <input
                                                                        type="color"
                                                                        className="form-control form-control-color"
                                                                        value={prize.textColor || '#ffffff'}
                                                                        onChange={(e) => handlePrizeChange(idx, 'textColor', e.target.value)}
                                                                    />
                                                                    <input
                                                                        type="text"
                                                                        className="form-control font-monospace"
                                                                        value={prize.textColor || '#ffffff'}
                                                                        onChange={(e) => handlePrizeChange(idx, 'textColor', e.target.value)}
                                                                    />
                                                                </div>
                                                            </div>

                                                            <div className="col-6 col-md-3">
                                                                <label className="form-label small fw-semibold text-secondary mb-1">Stock Total</label>
                                                                <input
                                                                    type="number"
                                                                    className="form-control form-control-sm fw-bold"
                                                                    min="0"
                                                                    value={prize.totalStock ?? 50}
                                                                    onChange={(e) => handlePrizeChange(idx, 'totalStock', Math.max(0, Number(e.target.value) || 0))}
                                                                />
                                                            </div>

                                                            <div className="col-6 col-md-3">
                                                                <label className="form-label small fw-semibold text-secondary mb-1">Peso Probabilidad</label>
                                                                <input
                                                                    type="number"
                                                                    className="form-control form-control-sm fw-bold"
                                                                    min="1"
                                                                    value={prize.probabilityWeight ?? 10}
                                                                    onChange={(e) => handlePrizeChange(idx, 'probabilityWeight', Math.max(1, Number(e.target.value) || 1))}
                                                                />
                                                            </div>
                                                        </div>

                                                        <div className="row g-2 align-items-center">
                                                            <div className="col-12 col-md-6">
                                                                <label className="form-label small fw-semibold text-secondary mb-1">Icono de Premio (Opcional)</label>
                                                                <div className="input-group input-group-sm">
                                                                    <input
                                                                        type="file"
                                                                        className="form-control"
                                                                        accept="image/*"
                                                                        onChange={(e) => handlePrizeIconUpload(e, idx)}
                                                                    />
                                                                    {prize.iconUrl && (
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-outline-danger"
                                                                            onClick={() => handlePrizeChange(idx, 'iconUrl', '')}
                                                                        >
                                                                            <i className="bi bi-trash"></i>
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            <div className="col-12 col-md-6 pt-2">
                                                                <div className="form-check form-switch">
                                                                    <input
                                                                        className="form-check-input"
                                                                        type="checkbox"
                                                                        id={`isWinnerSwitch_${idx}`}
                                                                        checked={prize.isWinner ?? true}
                                                                        onChange={(e) => handlePrizeChange(idx, 'isWinner', e.target.checked)}
                                                                    />
                                                                    <label className="form-check-label small fw-bold text-dark" htmlFor={`isWinnerSwitch_${idx}`}>
                                                                        {prize.isWinner ? '★ Es Premio Ganador' : 'Es Casilla de Cortesía (No Ganador)'}
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* TAB 4: CORTESÍA Y LEGAL */}
                                {activeTab === 'legal' && (
                                    <div className="d-flex flex-column gap-3">
                                        <div className="p-3 bg-light rounded-3 border">
                                            <h6 className="fw-bold text-dark mb-2"><i className="bi bi-pause-circle me-1.5"></i> Pantalla de Dinámica en Pausa (Cortesía)</h6>
                                            <div className="row g-2 mb-2">
                                                <div className="col-12 col-md-6">
                                                    <label className="form-label small fw-semibold text-secondary">Mensaje de Pausa (Español)</label>
                                                    <textarea
                                                        className="form-control form-control-sm"
                                                        rows={2}
                                                        value={courtesyMessageEs}
                                                        onChange={(e) => setCourtesyMessageEs(e.target.value)}
                                                    />
                                                </div>
                                                <div className="col-12 col-md-6">
                                                    <label className="form-label small fw-semibold text-secondary">Mensaje de Pausa (Inglés)</label>
                                                    <textarea
                                                        className="form-control form-control-sm"
                                                        rows={2}
                                                        value={courtesyMessageEn}
                                                        onChange={(e) => setCourtesyMessageEn(e.target.value)}
                                                    />
                                                </div>
                                            </div>

                                            <div className="row g-2">
                                                <div className="col-12 col-md-6">
                                                    <label className="form-label small fw-semibold text-secondary">URL de Redirección / Sitio Oficial</label>
                                                    <input
                                                        type="url"
                                                        className="form-control form-control-sm"
                                                        value={redirectUrl}
                                                        onChange={(e) => setRedirectUrl(e.target.value)}
                                                        placeholder="https://www.bajacup.com"
                                                    />
                                                </div>
                                                <div className="col-6 col-md-3">
                                                    <label className="form-label small fw-semibold text-secondary">Texto Botón (ES)</label>
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        value={redirectButtonTextEs}
                                                        onChange={(e) => setRedirectButtonTextEs(e.target.value)}
                                                    />
                                                </div>
                                                <div className="col-6 col-md-3">
                                                    <label className="form-label small fw-semibold text-secondary">Texto Botón (EN)</label>
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        value={redirectButtonTextEn}
                                                        onChange={(e) => setRedirectButtonTextEn(e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Editor de Términos y Condiciones */}
                                        <div className="p-3 bg-light rounded-3 border">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <h6 className="fw-bold text-dark mb-0">
                                                    <i className="bi bi-file-text me-1.5 text-danger"></i> Términos y Condiciones Oficiales
                                                </h6>
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary btn-sm py-0 px-2"
                                                    onClick={() => {
                                                        if (region === 'USA') {
                                                            setTermsAndConditionsTextEs(DEFAULT_TERMS_TEXT_ES);
                                                            setTermsAndConditionsTextEn(DEFAULT_TERMS_TEXT_EN);
                                                        } else {
                                                            setTermsAndConditionsTextEs(DEFAULT_TERMS_TEXT_MX_ES);
                                                            setTermsAndConditionsTextEn(DEFAULT_TERMS_TEXT_MX_EN);
                                                        }
                                                        showMessage({
                                                            type: MESSAGE_TYPE.INFO,
                                                            text: `Términos restablecidos al formato oficial de ${region === 'USA' ? 'USA' : 'México'}.`
                                                        });
                                                    }}
                                                >
                                                    <i className="bi bi-arrow-counterclockwise me-1"></i> Restaurar texto por defecto
                                                </button>
                                            </div>
                                            <div className="row g-2">
                                                <div className="col-12 col-md-6">
                                                    <label className="form-label small fw-semibold text-secondary">Términos y Condiciones (Español)</label>
                                                    <textarea
                                                        className="form-control form-control-sm font-monospace"
                                                        style={{ fontSize: '0.75rem' }}
                                                        rows={6}
                                                        value={termsAndConditionsTextEs}
                                                        onChange={(e) => setTermsAndConditionsTextEs(e.target.value)}
                                                    />
                                                </div>
                                                <div className="col-12 col-md-6">
                                                    <label className="form-label small fw-semibold text-secondary">Términos y Condiciones (Inglés)</label>
                                                    <textarea
                                                        className="form-control form-control-sm font-monospace"
                                                        style={{ fontSize: '0.75rem' }}
                                                        rows={6}
                                                        value={termsAndConditionsTextEn}
                                                        onChange={(e) => setTermsAndConditionsTextEn(e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Editor de Aviso de Privacidad */}
                                        <div className="p-3 bg-light rounded-3 border">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <h6 className="fw-bold text-dark mb-0">
                                                    <i className="bi bi-shield-lock me-1.5 text-danger"></i> Aviso de Privacidad y Consentimiento de Datos ({region === 'USA' ? 'CCPA/CPRA' : 'LFPDPPP'})
                                                </h6>
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary btn-sm py-0 px-2"
                                                    onClick={() => {
                                                        if (region === 'USA') {
                                                            setPrivacyPolicyTextEs(DEFAULT_PRIVACY_TEXT_ES);
                                                            setPrivacyPolicyTextEn(DEFAULT_PRIVACY_TEXT_EN);
                                                        } else {
                                                            setPrivacyPolicyTextEs(DEFAULT_PRIVACY_TEXT_MX_ES);
                                                            setPrivacyPolicyTextEn(DEFAULT_PRIVACY_TEXT_MX_EN);
                                                        }
                                                        showMessage({
                                                            type: MESSAGE_TYPE.INFO,
                                                            text: `Aviso de Privacidad restablecido al formato oficial de ${region === 'USA' ? 'USA' : 'México'}.`
                                                        });
                                                    }}
                                                >
                                                    <i className="bi bi-arrow-counterclockwise me-1"></i> Restaurar texto por defecto
                                                </button>
                                            </div>
                                            <div className="row g-2">
                                                <div className="col-12 col-md-6">
                                                    <label className="form-label small fw-semibold text-secondary">Aviso de Privacidad (Español)</label>
                                                    <textarea
                                                        className="form-control form-control-sm font-monospace"
                                                        style={{ fontSize: '0.75rem' }}
                                                        rows={6}
                                                        value={privacyPolicyTextEs}
                                                        onChange={(e) => setPrivacyPolicyTextEs(e.target.value)}
                                                    />
                                                </div>
                                                <div className="col-12 col-md-6">
                                                    <label className="form-label small fw-semibold text-secondary">Aviso de Privacidad (Inglés)</label>
                                                    <textarea
                                                        className="form-control form-control-sm font-monospace"
                                                        style={{ fontSize: '0.75rem' }}
                                                        rows={6}
                                                        value={privacyPolicyTextEn}
                                                        onChange={(e) => setPrivacyPolicyTextEn(e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* TAB 5: PERSONALIZACIÓN DE TARJETA DE GANADOR */}
                                {activeTab === 'winnerCard' && (
                                    <div className="d-flex flex-column gap-3">
                                        <div className="p-3 bg-light rounded-3 border">
                                            <h6 className="fw-bold text-dark mb-1">
                                                <i className="bi bi-card-checklist me-1.5 text-danger"></i> Elementos Visibles en la Tarjeta de Ganador
                                            </h6>
                                            <p className="text-secondary small mb-3">
                                                Selecciona qué secciones se mostrarán al aficionado cuando obtenga un premio ganador. Si desactivas una sección, quedará oculta en la pantalla del usuario.
                                            </p>

                                            <div className="d-flex flex-column gap-3">
                                                {/* Switch 1: Código QR de Canje */}
                                                <div className="p-3 bg-white rounded-3 border shadow-sm d-flex align-items-center justify-content-between">
                                                    <div>
                                                        <div className="fw-bold text-dark d-flex align-items-center gap-2">
                                                            <i className="bi bi-qr-code text-primary fs-5"></i>
                                                            <span>Código QR de Canje con Subtítulo</span>
                                                        </div>
                                                        <small className="text-secondary">
                                                            Muestra el código QR dinámico en pantalla junto con las instrucciones para que el staff lo escanee.
                                                        </small>
                                                    </div>
                                                    <div className="form-check form-switch ms-3">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            id="showVoucherQrSwitch"
                                                            style={{ width: '2.7em', height: '1.4em', cursor: 'pointer' }}
                                                            checked={showVoucherQr}
                                                            onChange={(e) => setShowVoucherQr(e.target.checked)}
                                                        />
                                                    </div>
                                                </div>

                                                {/* Switch 2: Folio Digital */}
                                                <div className="p-3 bg-white rounded-3 border shadow-sm d-flex align-items-center justify-content-between">
                                                    <div>
                                                        <div className="fw-bold text-dark d-flex align-items-center gap-2">
                                                            <i className="bi bi-upc-scan text-warning fs-5"></i>
                                                            <span>Recuadro de Folio Digital</span>
                                                        </div>
                                                        <small className="text-secondary">
                                                            Muestra la caja destacada con el código de folio (ej. #{folioPrefix || 'SD26'}-XXXX) y el nombre del premio obtenido.
                                                        </small>
                                                    </div>
                                                    <div className="form-check form-switch ms-3">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            id="showDigitalFolioSwitch"
                                                            style={{ width: '2.7em', height: '1.4em', cursor: 'pointer' }}
                                                            checked={showDigitalFolio}
                                                            onChange={(e) => setShowDigitalFolio(e.target.checked)}
                                                        />
                                                    </div>
                                                </div>

                                                {/* Switch 3: Botón Guardar en Galería */}
                                                <div className="p-3 bg-white rounded-3 border shadow-sm d-flex align-items-center justify-content-between">
                                                    <div>
                                                        <div className="fw-bold text-dark d-flex align-items-center gap-2">
                                                            <i className="bi bi-download text-success fs-5"></i>
                                                            <span>Botón "Guardar en Galería"</span>
                                                        </div>
                                                        <small className="text-secondary">
                                                            Habilita la descarga directa del cupón en formato imagen PNG (1080×1350 px) al dispositivo del aficionado.
                                                        </small>
                                                    </div>
                                                    <div className="form-check form-switch ms-3">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            id="showSaveToGalleryButtonSwitch"
                                                            style={{ width: '2.7em', height: '1.4em', cursor: 'pointer' }}
                                                            checked={showSaveToGalleryButton}
                                                            onChange={(e) => setShowSaveToGalleryButton(e.target.checked)}
                                                        />
                                                    </div>
                                                </div>

                                                {/* Switch 4: Tabla de Información del Aficionado */}
                                                <div className="p-3 bg-white rounded-3 border shadow-sm d-flex align-items-center justify-content-between">
                                                    <div>
                                                        <div className="fw-bold text-dark d-flex align-items-center gap-2">
                                                            <i className="bi bi-person-lines-fill text-info fs-5"></i>
                                                            <span>Tabla de Información del Participante</span>
                                                        </div>
                                                        <small className="text-secondary">
                                                            Muestra el desglose con el Nombre Completo, Fecha/Hora de participación y Estado de entrega.
                                                        </small>
                                                    </div>
                                                    <div className="form-check form-switch ms-3">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            id="showParticipantInfoSwitch"
                                                            style={{ width: '2.7em', height: '1.4em', cursor: 'pointer' }}
                                                            checked={showParticipantInfo}
                                                            onChange={(e) => setShowParticipantInfo(e.target.checked)}
                                                        />
                                                    </div>
                                                </div>

                                                {/* Switch 5: Instrucciones de Canje */}
                                                <div className="p-3 bg-white rounded-3 border shadow-sm d-flex align-items-center justify-content-between">
                                                    <div>
                                                        <div className="fw-bold text-dark d-flex align-items-center gap-2">
                                                            <i className="bi bi-info-circle text-secondary fs-5"></i>
                                                            <span>Instrucciones de Canje en Sitio</span>
                                                        </div>
                                                        <small className="text-secondary">
                                                            Muestra la caja informativa sobre dónde y cómo reclamar el premio físico dentro del recinto.
                                                        </small>
                                                    </div>
                                                    <div className="form-check form-switch ms-3">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            id="showRedemptionInstructionsSwitch"
                                                            style={{ width: '2.7em', height: '1.4em', cursor: 'pointer' }}
                                                            checked={showRedemptionInstructions}
                                                            onChange={(e) => setShowRedemptionInstructions(e.target.checked)}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </form>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="card-footer bg-light p-3 px-4 d-flex justify-content-between align-items-center border-top flex-shrink-0">
                        <button
                            type="button"
                            className="btn btn-outline-secondary px-3"
                            onClick={onClose}
                            disabled={isSaving || isCompressing}
                        >
                            Cancelar
                        </button>

                        <button
                            type="submit"
                            form="rouletteConfigForm"
                            className="btn btn-danger px-4 fw-bold text-white d-flex align-items-center gap-2 shadow-sm"
                            disabled={isSaving || isCompressing || isLoading}
                        >
                            {isSaving ? (
                                <>
                                    <span className="spinner-border spinner-border-sm" role="status"></span>
                                    <span>Guardando...</span>
                                </>
                            ) : isCompressing ? (
                                <>
                                    <span className="spinner-border spinner-border-sm" role="status"></span>
                                    <span>Optimizando Imagen...</span>
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-check-lg"></i>
                                    <span>Guardar Configuración</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default PersonalRouletteConfigModal;
